import { installMtstatSafeGuard } from './installMtstatSafeGuard';

const MTSTAT_SDK_SCRIPT_ATTR = 'data-mtstat-sdk';
const MTSTAT_SDK_CDN_URL = 'https://public.static.meitudata.com/meitu/mtstat-sdk/mtstat-sdk.min.js';
const MTSTAT_SDK_LOCAL_URL = '/mtstat-sdk.min.js';
const MTSTAT_SDK_LOAD_TIMEOUT_MS = 10000;

/**
 * 解析当前环境应使用的 mtstat-sdk 脚本地址；开发环境走本地 public 静态资源。
 */
export function getMtstatSdkScriptUrl() {
  return process.env.NEXT_PUBLIC_ENV === 'development'
    ? MTSTAT_SDK_LOCAL_URL
    : MTSTAT_SDK_CDN_URL;
}

/**
 * 等待页面中已插入的 mtstat-sdk script 标签完成加载。
 */
function waitForExistingMtstatScript(script: HTMLScriptElement): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.mtstat) {
      installMtstatSafeGuard();
      resolve();
      return;
    }

    const onLoad = () => {
      cleanup();
      installMtstatSafeGuard();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Failed to load mtstat-sdk'));
    };
    const timer = window.setTimeout(() => {
      cleanup();
      if (window.mtstat) {
        installMtstatSafeGuard();
        resolve();
        return;
      }
      reject(new Error('mtstat-sdk load timeout'));
    }, MTSTAT_SDK_LOAD_TIMEOUT_MS);

    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      window.clearTimeout(timer);
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
  });
}

/**
 * 通过 script 标签加载 mtstat-sdk，并统一设置 crossOrigin 为 anonymous。
 */
export function loadMtstatSdkScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  installMtstatSafeGuard();
  if (window.mtstat) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(
    `script[${MTSTAT_SDK_SCRIPT_ATTR}]`,
  ) as HTMLScriptElement | null;
  if (existingScript) {
    return waitForExistingMtstatScript(existingScript);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = getMtstatSdkScriptUrl();
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.setAttribute(MTSTAT_SDK_SCRIPT_ATTR, 'true');

    const timer = window.setTimeout(() => {
      cleanup();
      if (window.mtstat) {
        installMtstatSafeGuard();
        resolve();
        return;
      }
      reject(new Error('mtstat-sdk load timeout'));
    }, MTSTAT_SDK_LOAD_TIMEOUT_MS);

    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      window.clearTimeout(timer);
    };

    const onLoad = () => {
      cleanup();
      installMtstatSafeGuard();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Failed to load mtstat-sdk'));
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.head.appendChild(script);
  });
}
